import React from 'react';
import SVGIcon from '../../components/SVGIcon/SVGIcon';
import { Meta } from '../../styled components/Typography';
import {
  RefreshMenuItem,
  RefreshMenuItemMark,
  RefreshMenuSegment,
  RefreshMenuSurface,
  RefreshNowSegment,
  RefreshSplitButton
} from './ListViewRefresh/ListViewRefresh.style';
import { ToolbarDropdown } from './ListViewToolbar/ListViewToolbar.style';
import { refreshIntervalOptions } from '../../libs/listViewSettings';

/* How often the view refreshes itself, as the menu lists it */
function intervalLabel(intervalMinutes: number): string {
  if (intervalMinutes === 0) return i18n('Off');
  return i18n('Every {count} minutes', { count: intervalMinutes });
}

/* The intervals, then switching auto-refresh off: the menu reads as a scale
   with its far end last, whatever order the offered intervals are declared in */
const menuIntervals = [
  ...refreshIntervalOptions.filter((minutes) => minutes !== 0),
  ...refreshIntervalOptions.filter((minutes) => minutes === 0)
];

interface ListViewRefreshMenuProps {
  intervalMinutes: number;
  /* A refresh is on its way — by hand or by the clock. The glyph spins for
     both: what the user sees is the view catching up, not who asked */
  isRefreshing: boolean;
  onIntervalChange: (intervalMinutes: number) => void;
  onRefreshNow: () => void;
}

/**
 * The refresh button: the glyph refreshes the view right now and spins while a
 * refresh is running, and the caret next to it opens the menu holding the same
 * action and the auto-refresh interval to keep it on.
 */
const ListViewRefreshMenu = ({
  intervalMinutes,
  isRefreshing,
  onIntervalChange,
  onRefreshNow
}: ListViewRefreshMenuProps) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const close = () => setIsOpen(false);
  const refreshNow = () => {
    close();
    onRefreshNow();
  };
  const pickInterval = (minutes: number) => {
    close();
    onIntervalChange(minutes);
  };
  return (
    <div tw="relative flex items-center" data-test-list-view-refresh-menu>
      <RefreshSplitButton>
        <RefreshNowSegment
          type="button"
          $spinning={isRefreshing}
          disabled={isRefreshing}
          onClick={onRefreshNow}
          data-test-refresh-now
          aria-label={i18n('Refresh now')}
        >
          <SVGIcon icon="refresh" size="inherit" />
        </RefreshNowSegment>
        <RefreshMenuSegment
          type="button"
          onClick={() => setIsOpen((open) => !open)}
          data-test-refresh-menu-toggle
          aria-label={i18n('Refresh options')}
          aria-expanded={isOpen}
          aria-haspopup="menu"
        >
          <SVGIcon icon="expand-bottom-point" size="inherit" />
        </RefreshMenuSegment>
      </RefreshSplitButton>
      {isOpen && (
        <ToolbarDropdown $align="right" style={{ minWidth: '180px' }}>
          <RefreshMenuSurface role="menu" data-test-refresh-menu>

            {menuIntervals.map((minutes) => (
              <RefreshMenuItem
                key={minutes}
                type="button"
                onClick={() => pickInterval(minutes)}
                aria-checked={minutes === intervalMinutes}
                role="menuitemradio"
                data-test-refresh-interval={minutes}
              >
                <RefreshMenuItemMark>
                  {minutes === intervalMinutes && <SVGIcon icon="check" size="small" />}
                </RefreshMenuItemMark>
                <Meta>{intervalLabel(minutes)}</Meta>
              </RefreshMenuItem>
            ))}
          </RefreshMenuSurface>
        </ToolbarDropdown>
      )}
    </div>
  );
};

export default ListViewRefreshMenu;
